import React, { useState } from 'react';
import './Careers.css';

const Careers = () => {
  const [expandedJob, setExpandedJob] = useState(null);

  const jobs = [
    {
      id: 1,
      title: 'Senior Full Stack Engineer',
      department: 'Engineering',
      level: 'Senior',
      location: 'Remote',
      type: 'Full-time',
      description: 'We are looking for an experienced Full Stack Engineer to lead our platform development.',
      responsibilities: [
        'Design and implement scalable backend systems using Node.js and GraphQL',
        'Develop responsive frontend interfaces with React',
        'Mentor junior developers and conduct code reviews',
        'Collaborate with product team to define technical requirements',
        'Optimize database performance and implement caching strategies'
      ],
      requirements: [
        '5+ years of full stack development experience',
        'Strong proficiency in JavaScript/TypeScript',
        'Experience with React, Node.js, and GraphQL',
        'Database design and optimization skills',
        'Experience with cloud platforms (AWS, GCP, or Azure)',
        'Strong communication and leadership skills'
      ]
    },
    {
      id: 2,
      title: 'Senior Frontend Engineer',
      department: 'Engineering',
      level: 'Senior',
      location: 'Remote',
      type: 'Full-time',
      description: 'Lead our frontend development and build beautiful, scalable user interfaces for our co-founder matching platform.',
      responsibilities: [
        'Lead frontend architecture and technical decisions',
        'Build responsive web applications using React',
        'Mentor junior and mid-level frontend engineers',
        'Implement UI/UX designs with pixel-perfect accuracy',
        'Optimize application performance and user experience',
        'Conduct code reviews and establish best practices'
      ],
      requirements: [
        '5+ years of frontend development experience',
        'Expert-level React skills',
        'Strong CSS and responsive design knowledge',
        'Experience with state management (Zustand, Redux)',
        'Familiarity with GraphQL and REST APIs',
        'Leadership and mentoring experience',
        'Git and version control proficiency'
      ]
    },
    {
      id: 3,
      title: 'Senior Backend Engineer',
      department: 'Engineering',
      level: 'Senior',
      location: 'Remote',
      type: 'Full-time',
      description: 'Lead backend development and build robust, scalable systems that power our platform.',
      responsibilities: [
        'Lead backend architecture and technical strategy',
        'Develop and maintain GraphQL APIs',
        'Design and optimize database schemas',
        'Implement authentication and authorization systems',
        'Build real-time features and notifications',
        'Mentor backend engineers and conduct code reviews',
        'Ensure code quality and security best practices'
      ],
      requirements: [
        '5+ years of backend development experience',
        'Strong Node.js and JavaScript/TypeScript skills',
        'Experience with GraphQL and REST APIs',
        'Database design and SQL proficiency',
        'Understanding of microservices architecture',
        'Experience with Docker and deployment',
        'Leadership and mentoring experience'
      ]
    },
    {
      id: 4,
      title: 'Senior DevOps Engineer',
      department: 'Infrastructure',
      level: 'Senior',
      location: 'Remote',
      type: 'Full-time',
      description: 'Lead infrastructure management and optimize our cloud infrastructure and deployment pipelines.',
      responsibilities: [
        'Lead infrastructure strategy and architecture',
        'Manage cloud infrastructure on AWS/GCP',
        'Implement CI/CD pipelines and automation',
        'Monitor system performance and reliability',
        'Implement security best practices and compliance',
        'Manage databases and backups',
        'Mentor DevOps team members'
      ],
      requirements: [
        '5+ years of DevOps experience',
        'Strong cloud platform knowledge (AWS or GCP)',
        'Docker and Kubernetes expertise',
        'CI/CD pipeline experience',
        'Infrastructure as Code (Terraform, CloudFormation)',
        'Linux system administration skills',
        'Leadership and mentoring experience'
      ]
    },
    {
      id: 5,
      title: 'Senior Blockchain Engineer',
      department: 'Engineering',
      level: 'Senior',
      location: 'Remote',
      type: 'Full-time',
      description: 'Lead blockchain development and build decentralized solutions and smart contracts for our platform.',
      responsibilities: [
        'Lead blockchain architecture and strategy',
        'Design and develop smart contracts using Solidity',
        'Implement blockchain integration for platform features',
        'Optimize gas efficiency and contract security',
        'Conduct security audits and code reviews',
        'Collaborate with backend team on Web3 integration',
        'Mentor blockchain engineers'
      ],
      requirements: [
        '5+ years of blockchain development experience',
        'Strong Solidity and Web3.js knowledge',
        'Experience with Ethereum and other blockchains',
        'Understanding of smart contract security best practices',
        'Familiarity with DeFi protocols',
        'Experience with testing frameworks like Hardhat or Truffle',
        'Leadership and mentoring experience'
      ]
    },
    {
      id: 6,
      title: 'Senior AI/ML Engineer',
      department: 'Engineering',
      level: 'Senior',
      location: 'Remote',
      type: 'Full-time',
      description: 'Lead machine learning initiatives and develop advanced ML models to improve co-founder matching and recommendations.',
      responsibilities: [
        'Lead ML strategy and architecture',
        'Build and train advanced ML models for matching algorithms',
        'Develop recommendation systems using collaborative filtering',
        'Implement NLP for profile analysis and matching',
        'Optimize model performance and inference speed',
        'Conduct A/B testing and analyze model metrics',
        'Mentor ML engineers and conduct code reviews'
      ],
      requirements: [
        '5+ years of ML/AI development experience',
        'Strong Python and TensorFlow/PyTorch skills',
        'Experience with recommendation systems',
        'Knowledge of NLP and text processing',
        'Familiarity with data preprocessing and feature engineering',
        'Experience with model deployment and MLOps',
        'Leadership and mentoring experience'
      ]
    },
    {
      id: 7,
      title: 'Junior Developer',
      department: 'Engineering',
      level: 'Junior',
      location: 'Remote',
      type: 'Full-time',
      description: 'Start your career with us and grow as a developer in a supportive environment.',
      responsibilities: [
        'Develop features under senior developer guidance',
        'Write clean and maintainable code',
        'Participate in code reviews and learn best practices',
        'Fix bugs and improve existing features',
        'Contribute to documentation'
      ],
      requirements: [
        'Strong foundation in JavaScript/TypeScript',
        'Basic React or Node.js experience',
        'Understanding of web development fundamentals',
        'Git and version control knowledge',
        'Eagerness to learn and grow',
        'Good communication skills'
      ]
    }
  ];

  const toggleJob = (id) => {
    setExpandedJob(expandedJob === id ? null : id);
  };

  return (
    <div className="careers-page">
      <div className="careers-header">
        <h1 className="careers-title">Join Our Team</h1>
        <p className="careers-subtitle">
          Help us build the future of co-founder matching. We're looking for talented engineers and developers.
        </p>
      </div>

      <div className="careers-container">
        <div className="careers-intro">
          <div className="intro-card">
            <h2>Why Join CofounderHubs?</h2>
            <ul>
              <li>Work on a mission-driven platform connecting entrepreneurs globally</li>
              <li>Collaborate with talented and passionate team members</li>
              <li>Flexible remote work environment</li>
              <li>Competitive compensation and benefits</li>
              <li>Continuous learning and professional growth opportunities</li>
              <li>Impact millions of founders worldwide</li>
            </ul>
          </div>
        </div>

        <div className="careers-jobs">
          <h2 className="jobs-title">Open Positions</h2>
          <div className="jobs-list">
            {jobs.map(job => (
              <div key={job.id} className="job-card">
                <div 
                  className="job-header"
                  onClick={() => toggleJob(job.id)}
                >
                  <div className="job-title-section">
                    <h3 className="job-title">{job.title}</h3>
                    <div className="job-meta">
                      <span className="job-level">{job.level}</span>
                      <span className="job-location">{job.location}</span>
                      <span className="job-type">{job.type}</span>
                    </div>
                  </div>
                  <button className="job-toggle-btn">
                    {expandedJob === job.id ? '−' : '+'}
                  </button>
                </div>

                {expandedJob === job.id && (
                  <div className="job-details">
                    <p className="job-description">{job.description}</p>

                    <div className="job-section">
                      <h4>Responsibilities</h4>
                      <ul>
                        {job.responsibilities.map((resp, idx) => (
                          <li key={idx}>{resp}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="job-section">
                      <h4>Requirements</h4>
                      <ul>
                        {job.requirements.map((req, idx) => (
                          <li key={idx}>{req}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="job-actions">
                      <a href="mailto:careers@cofounderhubs.com" className="apply-btn">
                        Apply Now
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="careers-cta">
          <h2>Don't see a role that fits?</h2>
          <p>We're always looking for talented people. Send us your resume and let's talk!</p>
          <a href="mailto:careers@cofounderhubs.com" className="contact-btn">
            Send Your Resume
          </a>
        </div>
      </div>
    </div>
  );
};

export default Careers;
